import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { RootState } from '../redux/store';

export interface IAttachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  url?: string;
}

export interface IUploadAttachmentResponse {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  url: string;
}

export interface IAttachOrderAttachmentsRequest {
  orderId: string;
  attachmentIds: string[];
}

// ✅ Attachment Session Types
export interface IAttachmentSession {
  sessionId: number;
  ownerType: string;
  maxFiles: number;         // Backend decides limit
  allowedFormats: string;   // Backend returns allowed formats (e.g., 'pdf, xls, xlsx')
  expiresAt: number;
  status: 'OPEN' | 'FINALIZED' | 'EXPIRED';
  ttl: number;
}

export interface ISessionFile {
  fileId: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  path: string;
  uploadedAt: number;
}

export interface IUploadToSessionResponse {
  sessionId: number;
  fileId: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  path: string;
  uploadedAt: number;
}

export interface IGetSessionFilesResponse {
  sessionId: number;
  files: ISessionFile[];
  count: number;
}

export interface IValidateSessionResponse {
  sessionId: number;
  valid: boolean;
  status: string;
  filesCount: number;
}

export const attachmentsApi = createApi({
  reducerPath: 'attachmentsApi',
  tagTypes: ['Attachment'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    // Upload attachment (temporary)
    uploadAttachment: build.mutation<IUploadAttachmentResponse, FormData>({
      query: (formData) => ({
        url: '/attachments/upload',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: { data: IUploadAttachmentResponse }) =>
        response.data,
    }),

    // Get attachment by ID
    getAttachment: build.query<IAttachment, string>({
      query: (attachmentId) => ({
        url: `/attachments/${attachmentId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IAttachment }) => response.data,
      providesTags: (result, error, id) => [{ type: 'Attachment', id }],
    }),

    // Get attachments for an order
    getOrderAttachments: build.query<IAttachment[], string>({
      query: (orderId) => ({
        url: `/orders/${orderId}/attachments`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IAttachment[] }) => response.data,
      providesTags: (result, error, orderId) => [
        { type: 'Attachment', id: `ORDER_${orderId}` },
      ],
    }),

    // Delete attachment
    deleteAttachment: build.mutation<void, string>({
      query: (attachmentId) => ({
        url: `/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Attachment', id }],
    }),

    // Attach attachments to order (called after order creation)
    attachToOrder: build.mutation<void, IAttachOrderAttachmentsRequest>({
      query: ({ orderId, attachmentIds }) => ({
        url: `/orders/${orderId}/attachments`,
        method: 'POST',
        body: { attachmentIds },
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'Attachment', id: `ORDER_${orderId}` },
      ],
    }),

    // Download attachment (legacy - for session files)
    downloadAttachment: build.query<Blob, string>({
      query: (attachmentId) => ({
        url: `/attachments/${attachmentId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Download order attachment (from database)
    downloadOrderAttachment: build.query<Blob, { orderId: string; attachmentId: string }>({
      query: ({ orderId, attachmentId }) => ({
        url: `/orders/${orderId}/attachments/${attachmentId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ✅ CREATE ATTACHMENT SESSION
    createAttachmentSession: build.mutation<IAttachmentSession, { ownerType?: string; ttl?: number } | void>({
      query: (body) => ({
        url: '/attachments/session',
        method: 'POST',
        body: body || {},
      }),
      transformResponse: (response: { data: IAttachmentSession }) => response.data,
    }),

    // ✅ UPLOAD FILE TO SESSION
    uploadToSession: build.mutation<
      IUploadToSessionResponse,
      { sessionId: number; formData: FormData }
    >({
      query: ({ sessionId, formData }) => ({
        url: `/attachments/session/${sessionId}/upload`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: { data: IUploadToSessionResponse }) => response.data,
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'Attachment', id: `SESSION_${sessionId}` },
      ],
    }),

    // ✅ GET SESSION FILES
    getSessionFiles: build.query<IGetSessionFilesResponse, number>({
      query: (sessionId) => ({
        url: `/attachments/session/${sessionId}/files`,
        method: 'GET',
      }),
      transformResponse: (response: IGetSessionFilesResponse) => response,
      providesTags: (result, error, sessionId) => [
        { type: 'Attachment', id: `SESSION_${sessionId}` },
      ],
    }),

    // ✅ VALIDATE SESSION
    validateSession: build.query<IValidateSessionResponse, number>({
      query: (sessionId) => ({
        url: `/attachments/session/${sessionId}/validate`,
        method: 'GET',
      }),
      transformResponse: (response: IValidateSessionResponse) => response,
    }),

    // ✅ REMOVE FILE FROM SESSION
    removeFileFromSession: build.mutation<
      { success: boolean; sessionId: number; fileId: string },
      { sessionId: number; fileId: string }
    >({
      query: ({ sessionId, fileId }) => ({
        url: `/attachments/session/${sessionId}/file/${fileId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { data: { success: boolean; sessionId: number; fileId: string } }) => response.data,
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'Attachment', id: `SESSION_${sessionId}` },
      ],
    }),

    // ✅ CLEAR SESSION
    clearSession: build.mutation<void, number>({
      query: (sessionId) => ({
        url: `/attachments/session/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, sessionId) => [
        { type: 'Attachment', id: `SESSION_${sessionId}` },
      ],
    }),

    // ✅ DOWNLOAD FILE FROM SESSION
    downloadSessionFile: build.query<Blob, { sessionId: number; fileId: string }>({
      query: ({ sessionId, fileId }) => ({
        url: `/attachments/session/${sessionId}/file/${fileId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ✅ MARK EXISTING ATTACHMENT FOR DELETION IN SESSION
    // Deferred — actual delete runs on BindSession (Save)
    markSessionDeletion: build.mutation<
      { success: boolean },
      { sessionId: number; attachmentId: number }
    >({
      query: ({ sessionId, attachmentId }) => ({
        url: `/attachments/session/${sessionId}/mark-deletion`,
        method: 'PATCH',
        body: { attachmentId },
      }),
      transformResponse: (response: { data: { success: boolean } }) => response.data,
    }),
  }),
});

export const {
  useUploadAttachmentMutation,
  useGetAttachmentQuery,
  useGetOrderAttachmentsQuery,
  useDeleteAttachmentMutation,
  useAttachToOrderMutation,
  useLazyDownloadAttachmentQuery,
  useLazyDownloadOrderAttachmentQuery,
  // ✅ Session hooks
  useCreateAttachmentSessionMutation,
  useUploadToSessionMutation,
  useGetSessionFilesQuery,
  useValidateSessionQuery,
  useRemoveFileFromSessionMutation,
  useClearSessionMutation,
  useLazyDownloadSessionFileQuery,
  useMarkSessionDeletionMutation,
} = attachmentsApi;
